import { useState, useEffect } from 'react';
import { ClipboardCheck, Plus, FileText, Users, Shield, AlertTriangle, CheckCircle2, Clock, Trash2, Youtube, Play, Printer, History, Pencil, Sparkles, ShieldAlert, Heart, HandHeart, Droplet, Zap, Radiation, ArrowRight, Download } from 'lucide-react';
import Header from '../components/Header';
import BulkActions from '../components/BulkActions';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { SECTIONS } from '../lib/sectionTheme';
import axios from 'axios';
import { isSOPDraft } from '../lib/sopDraft';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const P = SECTIONS.sops; // Purple

function extractYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}


function YouTubeEmbed({ url }) {
  const id = extractYouTubeId(url);
  if (!id) return null;
  return (
    <div className="mt-4 relative w-full rounded-xl overflow-hidden border border-border" style={{ paddingBottom: '56.25%' }} data-testid="youtube-embed">
      <iframe
        className="absolute inset-0 w-full h-full"
        src={`https://www.youtube.com/embed/${id}`}
        title="SOP Video"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

export default function SOPsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('library');
  const [sops, setSops] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [audits, setAudits] = useState([]);
  const [stats, setStats] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [users, setUsers] = useState([]);
  const [expandedSop, setExpandedSop] = useState(null);
  const [editingSop, setEditingSop] = useState(null);
  const [versionsOpen, setVersionsOpen] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showAuditForm, setShowAuditForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', content: '', category: '', iso_reference: '', version: '1.0', video_url: '' });
  const [auditForm, setAuditForm] = useState({ auditor_name: '', auditor_company: '', audit_date: '', scope: '', findings: '', recommendations: '', compliance_score: 0, next_audit_date: '' });

  const isPM = user?.role === 'admin' || user?.role === 'superuser';

  useEffect(() => { if (!authLoading && user) loadData(); }, [authLoading, user]);

  const loadData = async () => {
    try {
      const headers = { withCredentials: true };
      const [s, a, au, st] = await Promise.all([
        axios.get(`${API}/sops`, headers), axios.get(`${API}/sops/assignments`, headers),
        axios.get(`${API}/audits`, headers), axios.get(`${API}/sops/stats`, headers),
      ]);
      setSops(s.data); setAssignments(a.data); setAudits(au.data); setStats(st.data);
      if (isPM) {
        const [c, u] = await Promise.all([
          axios.get(`${API}/sops/compliance`, headers), axios.get(`${API}/users`, headers),
        ]);
        setCompliance(c.data); setUsers(u.data);
      }
    } catch (e) { console.error('Failed to load SOP data'); }
  };

  const handleCreateSOP = async () => {
    try {
      if (editingSop) {
        await axios.put(`${API}/sops/${editingSop}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/sops`, formData, { withCredentials: true });
      }
      setFormData({ title: '', content: '', category: '', iso_reference: '', version: '1.0', video_url: '' });
      setShowForm(false); setEditingSop(null); loadData();
    } catch (e) { alert('Failed to save SOP'); }
  };

  const handleEditSOP = (sop) => {
    setFormData({ title: sop.title, content: sop.content, category: sop.category, iso_reference: sop.iso_reference, version: sop.version, video_url: sop.video_url || '' });
    setEditingSop(sop.sop_id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const loadVersions = async (sid) => {
    try {
      const r = await axios.get(`${API}/sops/${sid}/versions`, { withCredentials: true });
      setVersions(r.data);
      setVersionsOpen(sid);
    } catch { alert('Failed to load versions'); }
  };

  const handlePrintSOP = (sop) => {
    const w = window.open('', '_blank');
    const html = `
      <!DOCTYPE html><html><head><title>${sop.title}</title>
      <style>
        body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 0 40px; color: #0E2D5C; line-height: 1.6; }
        h1 { color: #0E2D5C; border-bottom: 3px solid #7C3AED; padding-bottom: 10px; }
        .meta { color: #64748B; font-size: 13px; margin-bottom: 24px; }
        .meta span { margin-right: 16px; }
        pre { white-space: pre-wrap; font-family: inherit; font-size: 14px; }
        .footer { margin-top: 48px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #64748B; text-align: center; }
      </style></head><body>
      <h1>${sop.title}</h1>
      <div class="meta">
        <span><strong>Category:</strong> ${sop.category || 'N/A'}</span>
        <span><strong>Version:</strong> ${sop.version}</span>
        ${sop.iso_reference ? `<span><strong>ISO:</strong> ${sop.iso_reference}</span>` : ''}
      </div>
      <pre>${sop.content.replace(/</g, '&lt;')}</pre>
      <div class="footer">SP Radiology Assistant — Sanctuary Point Ultrasound & Radiology · Generated ${new Date().toLocaleString()}</div>
      <script>window.onload = () => window.print();</script>
      </body></html>`;
    w.document.write(html); w.document.close();
  };

  const handleCreateAudit = async () => {
    try {
      await axios.post(`${API}/audits`, auditForm, { withCredentials: true });
      setAuditForm({ auditor_name: '', auditor_company: '', audit_date: '', scope: '', findings: '', recommendations: '', compliance_score: 0, next_audit_date: '' });
      setShowAuditForm(false); loadData();
    } catch (e) { alert('Failed to create audit'); }
  };

  const handleDeleteSOP = async (sid) => {
    if (!window.confirm('Delete this SOP?')) return;
    try { await axios.delete(`${API}/sops/${sid}`, { withCredentials: true }); loadData(); }
    catch { alert('Failed to delete'); }
  };

  const handlePublishSOP = async (sop) => {
    if (!window.confirm(
      `Publish "${sop.title}" as v1.0?\n\n` +
      `This will:\n` +
      `• Promote it from Draft to v1.0 (or bump major version if numeric)\n` +
      `• Remove any "## Status — To Be Developed (TBA)" block\n` +
      `• Make it available for staff assignment + count toward compliance\n` +
      `• Snapshot the previous version into history (recoverable)\n\n` +
      `Step-by-step walkthrough updates automatically — every assignment fetch re-parses the markdown.`
    )) return;
    try {
      const r = await axios.post(`${API}/sops/${sop.sop_id}/publish`, {}, { withCredentials: true });
      alert(`Published as v${r.data.version}.`);
      loadData();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Publish failed.');
    }
  };

  const handleAuditStatus = async (auditId, status) => {
    try {
      await axios.put(`${API}/audits/${auditId}/status`, { status, notes: '' }, { withCredentials: true });
      loadData();
    } catch { alert('Failed to update status'); }
  };

  if (authLoading) return <div className="min-h-screen bg-background"><Header /><div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full" style={{ borderColor: `${P.color} transparent ${P.color} ${P.color}` }} /></div></div>;

  const myAssignments = assignments.filter(a => a.user_id === user?.id);

  return (
    <div className="min-h-screen bg-background" data-testid="sops-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] mb-3" style={{ backgroundColor: P.colorBg, color: P.color, border: `1px solid ${P.ring}` }}>
              <ClipboardCheck className="w-3 h-3" /> SOPs & Compliance
            </div>
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-foreground" data-testid="sops-title">Standard Operating Procedures</h1>
            <p className="text-sm text-muted-foreground mt-1">Staff compliance, audit tracking, and ISO-aligned procedures</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={`${API}/sops/download-all.pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="sops-download-all"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl border shrink-0"
              style={{ borderColor: `${P.color}40`, color: P.color }}
            >
              <Download className="w-4 h-4" /> Download all (PDF)
            </a>
            <BulkActions resource="sops" accent={P.color} writable={isPM} onImported={loadData} />
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-testid="sops-stats">
            <StatCard label="Total SOPs" value={stats.total_sops} tone="neutral" />
            <StatCard label="Compliance" value={`${stats.compliance_rate}%`} tone={stats.compliance_rate >= 80 ? 'good' : stats.compliance_rate >= 50 ? 'warn' : 'bad'} />
            <StatCard label="Overdue" value={stats.overdue} tone={stats.overdue > 0 ? 'bad' : 'neutral'} />
            <StatCard label="Last audit" value={stats.latest_audit_score != null ? `${stats.latest_audit_score}%` : '—'} tone="neutral" purple />
          </div>
        )}

        {/* Ask the SOPs Assistant — quick prompts tile */}
        <AskAssistantTile isPM={isPM} />

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-border overflow-x-auto">
          {[
            { id: 'library', label: 'SOP Library', icon: FileText },
            ...(isPM ? [{ id: 'compliance', label: 'Compliance', icon: Users }] : []),
            { id: 'audits', label: 'Audits', icon: Shield },
          ].map(t => (
            <button key={t.id} data-testid={`sop-tab-${t.id}`} onClick={() => setTab(t.id)}
              className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors"
              style={tab === t.id ? { borderColor: P.color, color: P.color } : { borderColor: 'transparent', color: 'hsl(var(--muted-foreground))' }}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>

        {/* SOP Library */}
        {tab === 'library' && (
          <div data-testid="sop-library">
            {isPM && (
              <div className="flex items-center gap-2 mb-4">
                <button data-testid="sop-create-btn" onClick={() => setShowForm(!showForm)} className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors hover:opacity-90" style={{ backgroundColor: P.color }}>
                  <Plus className="w-4 h-4" /> Create SOP
                </button>
              </div>
            )}
            {showForm && (
              <div className="mb-6 bg-card border rounded-xl p-5" style={{ borderColor: P.ring }} data-testid="sop-create-form">
                <h3 className="text-sm font-semibold text-foreground mb-3">New Standard Operating Procedure</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <input placeholder="Title" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="px-3 py-2.5 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2" style={{ '--tw-ring-color': P.ring }} data-testid="sop-form-title" />
                  <input placeholder="Category (e.g., Radiation Safety)" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} className="px-3 py-2.5 text-sm bg-muted border border-border rounded-lg focus:outline-none" data-testid="sop-form-category" />
                  <input placeholder="ISO Reference (e.g., ISO 10789 Clause 4.2)" value={formData.iso_reference} onChange={e => setFormData({...formData, iso_reference: e.target.value})} className="px-3 py-2.5 text-sm bg-muted border border-border rounded-lg focus:outline-none" data-testid="sop-form-iso" />
                  <input placeholder="Version (e.g., 1.0)" value={formData.version} onChange={e => setFormData({...formData, version: e.target.value})} className="px-3 py-2.5 text-sm bg-muted border border-border rounded-lg focus:outline-none" data-testid="sop-form-version" />
                </div>
                <div className="relative mb-3">
                  <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#FF0000' }} />
                  <input placeholder="YouTube video URL (optional) — e.g., https://youtu.be/dQw4w9WgXcQ"
                    value={formData.video_url}
                    onChange={e => setFormData({...formData, video_url: e.target.value})}
                    className="w-full pl-10 pr-3 py-2.5 text-sm bg-muted border border-border rounded-lg focus:outline-none"
                    data-testid="sop-form-video" />
                </div>
                <textarea placeholder="SOP Content... (markdown supported)" value={formData.content} onChange={e => setFormData({...formData, content: e.target.value})} rows={6} className="w-full px-3 py-2.5 text-sm bg-muted border border-border rounded-lg resize-y mb-3" data-testid="sop-form-content" />
                <button onClick={handleCreateSOP} className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90" style={{ backgroundColor: P.color }} data-testid="sop-form-submit">Create SOP</button>
              </div>
            )}
            <div className="space-y-3">
              {sops.length === 0 && <p className="text-center py-12 text-muted-foreground">No SOPs created yet. {isPM && 'Click "Create SOP" to add one.'}</p>}
              {sops.map((sop, i) => {
                const isExpanded = expandedSop === sop.sop_id;
                return (
                  <div key={i} className="border border-border rounded-xl p-5 bg-card hover:shadow-sm transition-all" data-testid={`sop-item-${i}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: P.colorBg }}>
                          <FileText className="w-5 h-5" style={{ color: P.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-['Outfit'] text-base font-semibold text-foreground">{sop.title}</h3>
                            {isSOPDraft(sop) && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: '#F59E0B1A', color: '#B45309' }}
                                title="This SOP is a draft awaiting finalisation — content may change."
                                data-testid={`sop-draft-badge-${i}`}>
                                <AlertTriangle className="w-2.5 h-2.5" /> Draft
                              </span>
                            )}
                            {sop.video_url && extractYouTubeId(sop.video_url) && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/10 text-red-500" data-testid={`sop-has-video-${i}`}>
                                <Play className="w-2.5 h-2.5 fill-current" /> Video
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            <span>{sop.category}</span>
                            {sop.iso_reference && <span style={{ color: P.color }}>ISO: {sop.iso_reference}</span>}
                            <span>v{sop.version}</span>
                          </div>
                          <p className="text-sm text-foreground/80 mt-2 line-clamp-2">{sop.content}</p>
                          <button onClick={() => setExpandedSop(isExpanded ? null : sop.sop_id)} className="text-xs mt-2 font-medium hover:underline" style={{ color: P.color }} data-testid={`sop-expand-${i}`}>
                            {isExpanded ? 'Show less' : 'View full SOP' + (sop.video_url ? ' & video' : '')}
                          </button>
                          {isExpanded && (
                            <div className="mt-3 pt-3 border-t border-border" data-testid={`sop-expanded-${i}`}>
                              <pre className="text-sm text-foreground/80 whitespace-pre-wrap font-sans leading-relaxed">{sop.content}</pre>
                              {sop.video_url && <YouTubeEmbed url={sop.video_url} />}
                            </div>
                          )}
                        </div>
                      </div>
                      {isPM && (
                        <div className="flex items-center gap-1">
                          {isSOPDraft(sop) && (
                            <button onClick={() => handlePublishSOP(sop)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md text-white transition-colors mr-1"
                              style={{ backgroundColor: '#16A34A' }}
                              title="Promote this Draft to a published v1.0 — strips TBA status blocks. Step-by-step walkthrough updates automatically."
                              data-testid={`sop-publish-${i}`}>
                              <CheckCircle2 className="w-3 h-3" /> Publish
                            </button>
                          )}
                          <button onClick={() => handlePrintSOP(sop)} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors" title="Print / PDF" data-testid={`sop-print-${i}`}>
                            <Printer className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleEditSOP(sop)} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors" title="Edit" data-testid={`sop-edit-${i}`}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => loadVersions(sop.sop_id)} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors" title="Version history" data-testid={`sop-history-${i}`}>
                            <History className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteSOP(sop.sop_id)} className="p-1.5 text-muted-foreground hover:text-red-500 transition-colors" title="Delete" data-testid={`sop-delete-${i}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      {!isPM && (
                        <button onClick={() => handlePrintSOP(sop)} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors" title="Print / PDF" data-testid={`sop-print-${i}`}>
                          <Printer className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Assignments */}
        {tab === 'assignments' && (
          <div className="space-y-3" data-testid="sop-assignments">
            {(isPM ? assignments : myAssignments).length === 0 && <p className="text-center py-12 text-muted-foreground">No SOP assignments</p>}
            {(isPM ? assignments : myAssignments).map((a, i) => {
              const sop = sops.find(s => s.sop_id === a.sop_id);
              const isOverdue = a.status === 'pending' && a.due_date < new Date().toISOString().split('T')[0];
              return (
                <div key={i} className={`flex items-center justify-between p-4 border rounded-xl ${a.status === 'completed' ? 'border-green-500/30 bg-green-500/5' : isOverdue ? 'border-red-500/40 bg-red-500/5' : 'border-border bg-card'}`} data-testid={`assignment-${i}`}>
                  <div className="flex items-center gap-3">
                    {a.status === 'completed' ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : isOverdue ? <AlertTriangle className="w-5 h-5 text-red-600" /> : <Clock className="w-5 h-5 text-amber-500" />}
                    <div>
                      <p className="text-sm font-semibold text-foreground">{sop?.title || a.sop_id}</p>
                      <p className="text-xs text-muted-foreground">Due: {a.due_date} {isPM && `| User: ${a.user_id}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${a.status === 'completed' ? 'bg-green-500/10 text-green-600' : isOverdue ? 'bg-red-500/10 text-red-600' : 'bg-amber-500/10 text-amber-600'}`}>
                      {a.status === 'completed' ? 'Completed' : isOverdue ? 'Overdue' : 'Pending'}
                    </span>
                    {a.status === 'pending' && a.user_id === user?.id && (
                      <Link to={`/sops/walkthrough/${a.assignment_id}`} data-testid={`walkthrough-${i}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg"
                        style={{ backgroundColor: '#4338CA' }}>
                        Start walkthrough <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                    {a.status === 'completed' && a.user_id === user?.id && (
                      <Link to={`/sops/walkthrough/${a.assignment_id}`} data-testid={`walkthrough-review-${i}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-border text-foreground hover:bg-accent">
                        Review
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Compliance */}
        {tab === 'compliance' && compliance && (
          <div data-testid="sop-compliance">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <StatCard label="Completed" value={compliance.completed} tone="good" />
              <StatCard label="Pending" value={compliance.pending} tone="warn" />
              <StatCard label="Overdue" value={compliance.overdue} tone="bad" />
              <StatCard label="Overall Rate" value={`${compliance.rate}%`} tone={compliance.rate >= 80 ? 'good' : 'warn'} purple />
            </div>
            <h3 className="text-sm font-semibold text-foreground mb-3">Staff Compliance</h3>
            <div className="border border-border rounded-xl overflow-hidden bg-card">
              <table className="w-full text-sm">
                <thead><tr className="bg-muted border-b border-border">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Staff Member</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase">Assigned</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase">Completed</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase">Rate</th>
                </tr></thead>
                <tbody>
                  {compliance.users?.map((u, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="px-4 py-3"><span className="font-medium text-foreground">{u.name}</span><span className="text-xs text-muted-foreground ml-2">{u.email}</span></td>
                      <td className="px-4 py-3 text-center">{u.total}</td>
                      <td className="px-4 py-3 text-center">{u.completed}</td>
                      <td className="px-4 py-3 text-center"><span className={`font-bold ${u.rate >= 80 ? 'text-green-600' : u.rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{u.rate}%</span></td>
                    </tr>
                  ))}
                  {(!compliance.users || compliance.users.length === 0) && (
                    <tr><td colSpan="4" className="px-4 py-8 text-center text-muted-foreground">No staff assignments yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Audits */}
        {tab === 'audits' && (
          <div data-testid="sop-audits">
            {isPM && (
              <button data-testid="audit-create-btn" onClick={() => setShowAuditForm(!showAuditForm)} className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-white rounded-lg mb-4 hover:opacity-90" style={{ backgroundColor: P.color }}>
                <Plus className="w-4 h-4" /> Record Audit
              </button>
            )}
            {showAuditForm && (
              <div className="mb-6 bg-card border rounded-xl p-5" style={{ borderColor: P.ring }} data-testid="audit-form">
                <h3 className="text-sm font-semibold text-foreground mb-3">Record External Audit</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <input placeholder="Auditor Name" value={auditForm.auditor_name} onChange={e => setAuditForm({...auditForm, auditor_name: e.target.value})} className="px-3 py-2.5 text-sm bg-muted border border-border rounded-lg" data-testid="audit-form-name" />
                  <input placeholder="Auditing Company" value={auditForm.auditor_company} onChange={e => setAuditForm({...auditForm, auditor_company: e.target.value})} className="px-3 py-2.5 text-sm bg-muted border border-border rounded-lg" data-testid="audit-form-company" />
                  <div><label className="text-xs text-muted-foreground">Audit Date</label><input type="date" value={auditForm.audit_date} onChange={e => setAuditForm({...auditForm, audit_date: e.target.value})} className="w-full px-3 py-2.5 text-sm bg-muted border border-border rounded-lg" data-testid="audit-form-date" /></div>
                  <div><label className="text-xs text-muted-foreground">Next Audit Date</label><input type="date" value={auditForm.next_audit_date} onChange={e => setAuditForm({...auditForm, next_audit_date: e.target.value})} className="w-full px-3 py-2.5 text-sm bg-muted border border-border rounded-lg" data-testid="audit-form-next" /></div>
                  <input placeholder="Scope (e.g., ISO 10789 Full Audit)" value={auditForm.scope} onChange={e => setAuditForm({...auditForm, scope: e.target.value})} className="px-3 py-2.5 text-sm bg-muted border border-border rounded-lg" data-testid="audit-form-scope" />
                  <div><label className="text-xs text-muted-foreground">Compliance Score (%)</label><input type="number" min="0" max="100" value={auditForm.compliance_score} onChange={e => setAuditForm({...auditForm, compliance_score: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2.5 text-sm bg-muted border border-border rounded-lg" data-testid="audit-form-score" /></div>
                </div>
                <textarea placeholder="Findings..." value={auditForm.findings} onChange={e => setAuditForm({...auditForm, findings: e.target.value})} rows={3} className="w-full px-3 py-2.5 text-sm bg-muted border border-border rounded-lg mb-3" data-testid="audit-form-findings" />
                <textarea placeholder="Recommendations..." value={auditForm.recommendations} onChange={e => setAuditForm({...auditForm, recommendations: e.target.value})} rows={3} className="w-full px-3 py-2.5 text-sm bg-muted border border-border rounded-lg mb-3" data-testid="audit-form-recs" />
                <button onClick={handleCreateAudit} className="px-4 py-2 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: P.color }} data-testid="audit-form-submit">Record Audit</button>
              </div>
            )}
            <div className="space-y-4">
              {audits.length === 0 && <p className="text-center py-12 text-muted-foreground">No audits recorded. {isPM && 'Click "Record Audit" to log an external audit.'}</p>}
              {audits.map((a, i) => {
                const statusColors = { open: 'text-amber-600 bg-amber-500/10', 'in-progress': 'text-blue-600 bg-blue-500/10', closed: 'text-green-600 bg-green-500/10' };
                return (
                <div key={i} className="border border-border rounded-xl p-5 bg-card" data-testid={`audit-${i}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-['Outfit'] text-base font-semibold text-foreground">{a.auditor_company}</h3>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${statusColors[a.status || 'open']}`} data-testid={`audit-status-${i}`}>
                          {(a.status || 'open').replace('-', ' ')}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">Auditor: {a.auditor_name} | Date: {a.audit_date} | Scope: {a.scope}</p>
                    </div>
                    <div className={`text-center px-4 py-2 rounded-xl ${a.compliance_score >= 80 ? 'bg-green-500/10' : a.compliance_score >= 50 ? 'bg-amber-500/10' : 'bg-red-500/10'}`}>
                      <p className={`font-['Outfit'] text-xl font-bold ${a.compliance_score >= 80 ? 'text-green-600' : a.compliance_score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{a.compliance_score}%</p>
                      <p className="text-[10px] font-medium text-muted-foreground">Score</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><p className="text-xs font-bold uppercase text-muted-foreground mb-1">Findings</p><p className="text-sm text-foreground/80">{a.findings}</p></div>
                    <div><p className="text-xs font-bold uppercase text-muted-foreground mb-1">Recommendations</p><p className="text-sm text-foreground/80">{a.recommendations}</p></div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                    <p className="text-xs font-medium" style={{ color: P.color }}>Next audit scheduled: {a.next_audit_date}</p>
                    {isPM && (
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-muted-foreground">Status:</label>
                        <select value={a.status || 'open'} onChange={(e) => handleAuditStatus(a.audit_id, e.target.value)} className="text-xs px-2 py-1 bg-muted border border-border rounded" data-testid={`audit-status-select-${i}`}>
                          <option value="open">Open</option>
                          <option value="in-progress">In progress</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              );})}
            </div>
          </div>
        )}
      </div>

      {/* Versions Modal */}
      {versionsOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setVersionsOpen(null)} data-testid="versions-modal">
          <div className="bg-card border border-border rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="font-['Outfit'] text-lg font-semibold text-foreground flex items-center gap-2">
                <History className="w-5 h-5" style={{ color: P.color }} /> SOP version history
              </h3>
              <button onClick={() => setVersionsOpen(null)} className="text-muted-foreground hover:text-foreground text-xl" data-testid="versions-close">×</button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {versions.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No previous versions. Updates to this SOP will be tracked here.</p>}
              {versions.map((v, i) => (
                <div key={i} className="border border-border rounded-xl p-4 bg-muted/30" data-testid={`version-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: P.color }}>Version {v.version_idx} · v{v.version}</span>
                    <span className="text-xs text-muted-foreground">{new Date(v.snapshot_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm font-semibold text-foreground">{v.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{v.category} · Snapshot by {v.snapshot_by || 'system'}</p>
                  <pre className="text-xs text-foreground/80 whitespace-pre-wrap mt-2 line-clamp-4 font-sans">{v.content}</pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone = 'neutral', purple = false }) {
  const tones = {
    good: 'text-green-600',
    warn: 'text-amber-600',
    bad: 'text-red-600',
    neutral: 'text-foreground',
  };
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
      <p className={`font-['Outfit'] text-2xl font-bold ${tones[tone]}`} style={purple ? { color: SECTIONS.sops.color } : {}}>{value}</p>
    </div>
  );
}

// Quick-action tile that opens the chat bubble pre-filled with a curated prompt,
// drawn from the SP Quality Manual + Wait List + SP Workflow SOP content.
function AskAssistantTile({ isPM }) {
  const P = SECTIONS.sops;
  return (
    <div className="mb-8 space-y-3">
      {/* Governance documents — 9 standalone reference cards (admin/PM only; staff get walkthroughs via My Tasks) */}
      {isPM && (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* 1. Work Culture */}
        <a
          href="/sops/work-culture"
          data-testid="view-work-culture"
          className="flex items-center gap-3 rounded-2xl border-2 p-4 hover:shadow-sm transition-all"
          style={{ borderColor: '#047857', backgroundColor: '#0478570A' }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-white" style={{ backgroundColor: '#047857' }}>
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-['Outfit'] text-sm font-bold text-foreground">Work Culture &amp; Philosophy</h3>
              <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: '#047857' }}>Charter</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">Closed-loop care · values · conduct.</p>
          </div>
        </a>

        {/* 2. Anaphylaxis */}
        <a
          href="/sops/anaphylaxis"
          data-testid="view-anaphylaxis"
          className="flex items-center gap-3 rounded-2xl border-2 p-4 hover:shadow-sm transition-all"
          style={{ borderColor: '#DC2626', backgroundColor: '#DC26260A' }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-white" style={{ backgroundColor: '#DC2626' }}>
            <Zap className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-['Outfit'] text-sm font-bold text-foreground">Anaphylaxis Protocol</h3>
              <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: '#DC2626' }}>USE FIRST</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">Dose chart · tray layout · 10-min drill.</p>
          </div>
        </a>

        {/* 3. Quality Manual */}
        <a
          href="/sops/quality-manual"
          data-testid="view-quality-manual"
          className="flex items-center gap-3 rounded-2xl border p-4 bg-card hover:shadow-sm transition-all"
          style={{ borderColor: `${P.color}33` }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${P.color}1A`, color: P.color }}>
            <FileText className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-['Outfit'] text-sm font-semibold text-foreground">Quality Manual</h3>
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${P.color}1A`, color: P.color }}>DIAS</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">Full IR governance framework.</p>
          </div>
        </a>

        {/* 4. Urgent Findings */}
        <a
          href="/sops/urgent-findings"
          data-testid="view-urgent-findings"
          className="flex items-center gap-3 rounded-2xl border p-4 bg-card hover:shadow-sm transition-all"
          style={{ borderColor: '#DC262633' }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#DC262615', color: '#DC2626' }}>
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-['Outfit'] text-sm font-semibold text-foreground">Urgent Findings</h3>
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#DC262615', color: '#DC2626' }}>RIS phrases</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">Tiered communication · ED escalation.</p>
          </div>
        </a>

        {/* 5. Clinical Episodes */}
        <a
          href="/sops/clinical-episodes"
          data-testid="view-clinical-episodes"
          className="flex items-center gap-3 rounded-2xl border p-4 bg-card hover:shadow-sm transition-all"
          style={{ borderColor: '#D9770633' }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#D9770615', color: '#D97706' }}>
            <Heart className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-['Outfit'] text-sm font-semibold text-foreground">Clinical Episodes</h3>
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#D9770615', color: '#D97706' }}>Vasovagal</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">5-step response · patient scripts.</p>
          </div>
        </a>

        {/* 6. Difficult Patients */}
        <a
          href="/sops/difficult-patients"
          data-testid="view-difficult-patients"
          className="flex items-center gap-3 rounded-2xl border p-4 bg-card hover:shadow-sm transition-all"
          style={{ borderColor: '#0D948833' }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#0D948815', color: '#0D9488' }}>
            <HandHeart className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-['Outfit'] text-sm font-semibold text-foreground">Difficult Patients</h3>
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#0D948815', color: '#0D9488' }}>De-escalation</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">Staff scripts · 3-level chart.</p>
          </div>
        </a>

        {/* 7. Contrast SOP */}
        <a
          href="/sops/contrast"
          data-testid="view-contrast-sop"
          className="flex items-center gap-3 rounded-2xl border p-4 bg-card hover:shadow-sm transition-all"
          style={{ borderColor: '#4338CA33' }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#4338CA15', color: '#4338CA' }}>
            <Droplet className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-['Outfit'] text-sm font-semibold text-foreground">Contrast SOP</h3>
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#4338CA15', color: '#4338CA' }}>RANZCR</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">eGFR · metformin · reaction chart.</p>
          </div>
        </a>

        {/* 8. Dose Audit */}
        <a
          href="/sops/radiation-audit"
          data-testid="view-radiation-audit"
          className="flex items-center gap-3 rounded-2xl border p-4 bg-card hover:shadow-sm transition-all"
          style={{ borderColor: '#4338CA33' }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#4338CA15', color: '#4338CA' }}>
            <Radiation className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-['Outfit'] text-sm font-semibold text-foreground">Dose Audit &amp; DRLs</h3>
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#4338CA15', color: '#4338CA' }}>ARPANSA</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">CT outlier flagging · incident log.</p>
          </div>
        </a>

        {/* 9. Governance Dashboard */}
        <a
          href="/sops/governance"
          data-testid="view-governance-dashboard"
          className="flex items-center gap-3 rounded-2xl border p-4 bg-card hover:shadow-sm transition-all"
          style={{ borderColor: `${P.color}55`, backgroundColor: `${P.color}08` }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${P.color}15`, color: P.color }}>
            <Shield className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-['Outfit'] text-sm font-semibold text-foreground">Governance Dashboard</h3>
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${P.color}1A`, color: P.color }}>Medical Director</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">Version · review dates · audit sign-off.</p>
          </div>
        </a>

        {/* 10. IPC Tooling */}
        <a
          href="/ipc"
          data-testid="view-ipc-tools"
          className="flex items-center gap-3 rounded-2xl border p-4 bg-card hover:shadow-sm transition-all"
          style={{ borderColor: '#0EA5E955' }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#0EA5E915', color: '#0EA5E9' }}>
            <ClipboardCheck className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-['Outfit'] text-sm font-semibold text-foreground">IPC Tooling</h3>
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#0EA5E915', color: '#0EA5E9' }}>Audit-ready</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">Cleaning logs · bottle register · monthly audits.</p>
          </div>
        </a>
      </div>
      )}

      {/* Chatbot hint strip */}
      <div data-testid="sops-chat-hint"
        className="rounded-xl border flex items-center gap-2 px-4 py-2.5"
        style={{ borderColor: `${P.color}40`, backgroundColor: `${P.color}08` }}>
        <Sparkles className="w-4 h-4 shrink-0" style={{ color: P.color }} />
        <p className="text-xs text-muted-foreground">
          Use the <span className="font-semibold" style={{ color: P.color }}>SOPs Assist</span> chatbot in the bottom-right corner for instant answers on SOPs, audits and compliance.
        </p>
      </div>
    </div>
  );
}
